import { object, number, string, boolean } from 'yup';

import { validationMessages } from './messages';
import { buildMessageCallback } from '../../../validation/utilities';

const PRODUCT_NAME_MAX_LENGTH = 200;
const PRODUCT_SKU_MAX_LENGTH = 200;
const WAREHOUSE_LOCATION_MAX_LENGTH = 100;
const CUSTOMS_VALUE_MAX = 999999;
const MID_CODE_MAX_LENGTH = 15;
const UPC_MAX_LENGTH = 50;
const CPSC_CERTIFICATE_FIELD_MAX_LENGTH = 50;

// SKU validation for new products (required)
const newProductSkuValidation = string()
  .meta({ fieldName: 'productSku' })
  .transform((value, originalValue) => (!originalValue ? null : value))
  .required(buildMessageCallback(validationMessages.productSkuRequired))
  .max(
    PRODUCT_SKU_MAX_LENGTH,
    buildMessageCallback(validationMessages.productSkuTooLong, {
      values: { maxLength: PRODUCT_SKU_MAX_LENGTH },
    }),
  );

// SKU validation for existing products (optional)
const existingProductSkuValidation = string()
  .meta({ fieldName: 'productSku' })
  .max(
    PRODUCT_SKU_MAX_LENGTH,
    buildMessageCallback(validationMessages.productSkuTooLong, {
      values: { maxLength: PRODUCT_SKU_MAX_LENGTH },
    }),
  )
  .nullable();

// Factory function to create schema with conditional SKU validation
export const createProductDetailsSchema = (isNewProduct, isLtlProductFieldsEnabled = false) =>
  object({
    // Product Name - Backend requires non-empty, max 200 characters (database: nvarchar(200))
    productName: string()
      .meta({ fieldName: 'productName' })
      .transform((value, originalValue) => (!originalValue ? null : value))
      .required(buildMessageCallback(validationMessages.productNameRequired))
      .max(
        PRODUCT_NAME_MAX_LENGTH,
        buildMessageCallback(validationMessages.productNameTooLong, {
          values: { maxLength: PRODUCT_NAME_MAX_LENGTH },
        }),
      ),

    // Product SKU - Backend requires non-empty for new products only, max 200 characters (database: nvarchar(200))
    productSku: isNewProduct ? newProductSkuValidation : existingProductSkuValidation,

    // UPC - Backend enforces max 50 characters
    upc: string()
      .nullable()
      .meta({ fieldName: 'upc' })
      .transform((value, originalValue) => (!originalValue ? null : value))
      .max(
        UPC_MAX_LENGTH,
        buildMessageCallback(isLtlProductFieldsEnabled ? validationMessages.barcodeTooLong : validationMessages.upcTooLong, {
          values: { maxLength: UPC_MAX_LENGTH },
        }),
      ),

    // Warehouse Location - Backend enforces max 100 characters
    warehouseLocation: string()
      .nullable()
      .meta({ fieldName: 'warehouseLocation' })
      .transform((value, originalValue) => (!originalValue ? null : value))
      .max(
        WAREHOUSE_LOCATION_MAX_LENGTH,
        buildMessageCallback(validationMessages.warehouseLocationTooLong, {
          values: { maxLength: WAREHOUSE_LOCATION_MAX_LENGTH },
        }),
      ),

    // Declared Value - Backend enforces max 999,999
    // Note: CurrencyInput returns strings like "1000.00", so we need to parse them
    customsDeclaredValue: number()
      .nullable()
      .transform((value, originalValue) => {
        // Handle empty/null/undefined
        if (!originalValue) {
          return null;
        }
        // Parse string to number (CurrencyInput uses toFixed which returns a string)
        const parsed = typeof originalValue === 'string' ? parseFloat(originalValue) : originalValue;
        return isNaN(parsed) ? null : parsed;
      })
      .meta({ fieldName: 'customsDeclaredValue' })
      .max(
        CUSTOMS_VALUE_MAX,
        buildMessageCallback(validationMessages.customsValueTooHigh, {
          values: { maxValue: CUSTOMS_VALUE_MAX.toLocaleString() },
        }),
      ),

    // MID Code - Backend enforces max 15 chars (UI has maxLength) and alphanumeric only
    customsMIDCode: string()
      .nullable()
      .meta({ fieldName: 'customsMIDCode' })
      .transform((value, originalValue) => (!originalValue ? null : value))
      .test({
        name: 'alphanumeric',
        message: buildMessageCallback(validationMessages.midCodeInvalidCharacters),
        test: (value) => {
          if (!value) return true;
          return /^[a-zA-Z0-9]*$/.test(value);
        },
      }),

    isMultiPackageEnabled: boolean(),

    // Package Set - required once "Ships in Multiple Packages" is enabled
    packageSetId: string()
      .nullable()
      .meta({ fieldName: 'packageSetId' })
      .when('isMultiPackageEnabled', {
        is: true,
        then: (schema) => schema.required(buildMessageCallback(validationMessages.packageSetRequired)),
      }),
  });

// Export constants for use in components
export { MID_CODE_MAX_LENGTH, UPC_MAX_LENGTH, CPSC_CERTIFICATE_FIELD_MAX_LENGTH };
